import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, Mail } from "lucide-react";
import officialLogo from "@/assets/official-wle-logo.webp.asset.json";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About Us" },
    { path: "/products", label: "Products" },
    { path: "/services", label: "Services", hasSubmenu: true },
    { path: "/tools", label: "Tools", hasSubmenu: true },
    { path: "/portfolio", label: "Portfolio" },
    { path: "/industries", label: "Industries" },
    { path: "/affiliations", label: "Affiliations" },
    { path: "/contact", label: "Contact" },
  ];

  const serviceItems = [
    { path: "/services/tank-fabrication", label: "Tank Fabrication" },
    { path: "/services/mechanical-polishing", label: "Mechanical Polishing" },
    { path: "/services/electropolishing", label: "Electropolishing" },
  ];

  const toolItems = [
    { path: "/tools/tank-volume-calculator", label: "Tank Volume Calculator" },
    { path: "/tools/tank-weight-calculator", label: "Tank Weight Calculator" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-20 items-center justify-between px-8">
        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center" aria-label="WLE home">
          <img
            src={officialLogo.url}
            alt="WLE — People, Process, A Cleaner Tomorrow"
            className="h-auto w-36 sm:w-40 object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center space-x-8">
          {navItems.map((item) => (
            <div key={item.path} className="relative group">
              <Link
                to={item.path}
                className={`text-sm font-medium transition-colors hover:text-primary ${
                  isActive(item.path) ? "text-primary" : "text-foreground/60"
                }`}
              >
                {item.label}
              </Link>
              {item.hasSubmenu && (
                <div className="absolute top-full left-0 mt-2 w-56 rounded-md border bg-popover p-1 shadow-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  {item.label === "Services" && serviceItems.map((service) => (
                    <Link
                      key={service.path}
                      to={service.path}
                      className="block px-3 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground"
                    >
                      {service.label}
                    </Link>
                  ))}
                  {item.label === "Tools" && toolItems.map((tool) => (
                    <Link
                      key={tool.path}
                      to={tool.path}
                      className="block px-3 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground"
                    >
                      {tool.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Info */}
        <div className="hidden lg:flex items-center space-x-6">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Phone className="h-4 w-4" />
            <span>+6011 3906 9019</span>
          </div>
          <Button asChild>
            <Link to="/contact">Get Quote</Link>
          </Button>
        </div>

        {/* Mobile Navigation */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle navigation menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80">
            <div className="flex flex-col space-y-4 mt-4">
              {navItems.map((item) => (
                <div key={item.path}>
                  <Link
                    to={item.path}
                    className={`block py-2 text-lg font-medium transition-colors hover:text-primary ${
                      isActive(item.path) ? "text-primary" : "text-foreground"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {item.label}
                  </Link>
                  {item.hasSubmenu && (
                    <div className="ml-4 space-y-2">
                      {item.label === "Services" && serviceItems.map((service) => (
                        <Link
                          key={service.path}
                          to={service.path}
                          className="block py-1 text-sm text-muted-foreground hover:text-primary"
                          onClick={() => setIsOpen(false)}
                        >
                          {service.label}
                        </Link>
                      ))}
                      {item.label === "Tools" && toolItems.map((tool) => (
                        <Link
                          key={tool.path}
                          to={tool.path}
                          className="block py-1 text-sm text-muted-foreground hover:text-primary"
                          onClick={() => setIsOpen(false)}
                        >
                          {tool.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="pt-4 border-t space-y-4">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>+6011 3906 9019</span>
                </div>
                <Button asChild className="w-full">
                  <Link to="/contact" onClick={() => setIsOpen(false)}>Get Quote</Link>
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </nav>
  );
};

export default Navigation;